import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { Sparkles } from 'lucide-react';
import type { DemoCoupon } from '@staff-app/shared-types';
import {
  useCreateDemoCoupon,
  useDemoCouponQuote,
  useDemoDeviceOptions,
} from '../hooks/useDemoCoupons';

const money = (n: number) => `$${n.toFixed(2)}`;
const MAX_QUANTITY = 10; // mirrors the service and the CHECK in migration 019

/** One label/value line of the calculation. */
function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`flex justify-between gap-4 ${strong ? 'font-medium text-foreground' : ''}`}>
      <dt className="truncate">{label}</dt>
      <dd className="shrink-0 font-mono">{value}</dd>
    </div>
  );
}

/**
 * The demo-coupon card: device · quantity · US/International · reason → a single-use Shopify code.
 *
 * The quote is shown before minting for the same reason the invoice path shows one: the operator
 * is about to hand out money and should see how much before they do, not after.
 */
export function DemoCouponForm({ onCreated }: { onCreated: (c: DemoCoupon) => void }) {
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [customerEmail, setCustomerEmail] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [edited, setEdited] = useState(false);

  const { options, loading: loadingDevices } = useDemoDeviceOptions();
  const qty = Number(quantity);
  const { data: quoteData, loading: quoting, error: quoteError } = useDemoCouponQuote(sku, qty);
  const quote = quoteData?.demoCouponQuote;

  // Adopt the quote unless the operator has typed their own figure.
  useEffect(() => {
    if (!edited && quote) setAmount(String(quote.totalAmount));
  }, [quote, edited]);

  const [create, { loading: creating, error: createError }] = useCreateDemoCoupon();

  const amountNum = Number(amount);
  // Mirrors the server's rule exactly (demo-coupons.service.ts) — permissive on purpose: this is
  // a record of who the code went to, not a login, so a stricter pattern would reject real
  // addresses to protect nothing.
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());
  const canSubmit =
    !!sku &&
    qty >= 1 &&
    qty <= MAX_QUANTITY &&
    emailLooksValid &&
    reason.trim().length >= 3 &&
    amountNum > 0 &&
    !creating;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const res = await create({
        variables: {
          input: {
            sku,
            quantity: qty,
            customerEmail: customerEmail.trim(),
            reason: reason.trim(),
            // Only send an amount when it was actually changed — otherwise the server re-reads
            // the catalog price, which is the figure we want on a stale tab.
            ...(edited && amountNum > 0 ? { amount: amountNum } : {}),
          },
        },
      });
      const created = res.data?.createDemoCoupon;
      if (!created) return;
      onCreated(created);
      // Clear only what is specific to THIS coupon. The device stays: issuing several demo codes
      // in a row is the normal case, and re-picking the same one each time is friction. The
      // customer does NOT stay — the next code is usually for someone else, and a leftover
      // address is how one customer's name ends up on another's coupon.
      setCustomerEmail('');
      setReason('');
      setAmount('');
      setEdited(false);
    } catch {
      // Surfaced to the operator through `createError` below. Swallowed here because an
      // unhandled rejection is not an error message — it is a blank card and a console entry.
    }
  };

  return (
    <div className="rounded-lg border p-4" data-testid="demo-coupon-form">
      <h3 className="flex items-center gap-2 font-semibold">
        <Sparkles className="h-4 w-4" />
        {'Create a demo coupon'}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {'A single-use Shopify code the customer redeems in the portal’s Order Devices checkout. '}
        {'US shipping only. It expires in 30 days, and anyone holding the code can redeem it — '}
        {'the email below is the record of who it was issued to, not a restriction on who can use it.'}
      </p>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {/* FIRST, by the requester (2026-08-24) — and it matches how the job actually starts: a
            named person asked for a demo, and everything after this field is describing what to
            send them. */}
        <div className="space-y-2">
          <Label htmlFor="demo-customer-email">{'Customer email'}</Label>
          <Input
            id="demo-customer-email"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="dana@acme-schools.org"
            data-testid="demo-customer-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="demo-device">{'Device'}</Label>
          <Select value={sku} onValueChange={setSku}>
            <SelectTrigger id="demo-device" data-testid="demo-device-select">
              <SelectValue placeholder={loadingDevices ? 'Loading catalog…' : 'Select a device'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.sku} value={o.sku}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="demo-qty">{`Quantity (1–${MAX_QUANTITY})`}</Label>
          <Input
            id="demo-qty"
            type="number"
            min={1}
            max={MAX_QUANTITY}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            data-testid="demo-quantity"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="demo-amount">{'Coupon value (USD)'}</Label>
          <Input
            id="demo-amount"
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => {
              setEdited(true);
              setAmount(e.target.value);
            }}
            placeholder={quoting ? 'Pricing…' : ''}
            data-testid="demo-amount"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="demo-reason">{'Reason'}</Label>
          <Input
            id="demo-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Who is this for, and why — e.g. “demo unit for Acme Schools, requested by Dana”"
            data-testid="demo-reason"
          />
          <p className="text-xs text-muted-foreground">
            {'Nobody approves these — the reason is the whole record of why the code exists.'}
          </p>
        </div>
      </div>

      {quote ? (
        <dl className="mt-4 space-y-1 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          <Row label={`${quote.deviceName} × ${quote.quantity}`} value={money(quote.deviceAmount)} />
          <Row
            label={
              quote.shippingUnitPrice != null
                ? `Configuration & Delivery (${money(quote.shippingUnitPrice)} × ${quote.quantity}, US)`
                : 'Configuration & Delivery (US)'
            }
            value={money(quote.shippingAmount)}
          />
          <Row label="Coupon value" value={money(quote.totalAmount)} strong />
          <p className="pt-1 text-xs">{'Sales tax is not included — the customer pays tax at checkout.'}</p>
        </dl>
      ) : null}

      {quoteError ? (
        <p className="mt-3 text-sm text-destructive" data-testid="demo-quote-error">
          {quoteError.message}
        </p>
      ) : null}
      {createError ? (
        <p className="mt-3 text-sm text-destructive" data-testid="demo-create-error">
          {createError.message}
        </p>
      ) : null}

      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => {
            void submit();
          }}
          disabled={!canSubmit}
          data-testid="demo-create"
        >
          {creating ? 'Creating…' : 'Create coupon'}
        </Button>
      </div>
    </div>
  );
}
